// src/hooks/useESignWatcher.js
//
// Watches a Leegality signing session without polling Leegality.
//
//   * The backend learns the outcome from Leegality's webhook (or the signer's
//     return page) and pushes `esign.updated` over the realtime channel.
//   * Hints that something may have changed — the signing popup closing, the
//     return page's postMessage, the tab regaining focus — make the caller
//     re-read ITS OWN record (our API, free).
//   * Only while the realtime channel is down does it re-read on a slow timer.
//
// onSignal(payload|null): payload is the pushed event, null for a hint.

import { useCallback, useEffect, useRef, useState } from 'react';
import notificationStream from '../modules/notifications/stream';

const POPUP_WATCH_MS = 1000;
const DEGRADED_RESYNC_MS = 15000;

export default function useESignWatcher({ documentId, active, onSignal }) {
  const popupRef = useRef(null);
  const watchRef = useRef(null);
  const onSignalRef = useRef(onSignal);
  const [popupBlocked, setPopupBlocked] = useState(false);
  const [popupClosed, setPopupClosed] = useState(false);
  const [streamStatus, setStreamStatus] = useState(notificationStream.status);

  useEffect(() => { onSignalRef.current = onSignal; }, [onSignal]);
  const signal = useCallback((payload = null) => onSignalRef.current?.(payload), []);

  const stopWatch = useCallback(() => {
    if (watchRef.current) { clearInterval(watchRef.current); watchRef.current = null; }
  }, []);

  const closePopup = useCallback(() => {
    stopWatch();
    try { if (popupRef.current && !popupRef.current.closed) popupRef.current.close(); } catch (e) { /* cross-origin */ }
    popupRef.current = null;
  }, [stopWatch]);

  /** Open the signing page in a tracked popup (not a detached tab). */
  const open = useCallback((url) => {
    if (!url) return false;
    const width = 980;
    const height = 760;
    const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2);
    const top = window.screenY + Math.max(0, (window.outerHeight - height) / 2);
    const popup = window.open(url, 'leegality_esign', `width=${width},height=${height},left=${left},top=${top}`);
    if (!popup) { setPopupBlocked(true); return false; }
    popupRef.current = popup;
    setPopupBlocked(false);
    setPopupClosed(false);
    stopWatch();
    watchRef.current = setInterval(() => {
      if (popupRef.current && popupRef.current.closed) {
        stopWatch();
        setPopupClosed(true);
        signal(null);
      }
    }, POPUP_WATCH_MS);
    return true;
  }, [signal, stopWatch]);

  // Primary: server push for this document.
  useEffect(() => {
    if (!active || !documentId) return undefined;
    const off = notificationStream.subscribe(({ event, payload }) => {
      if (event === 'esign.updated' && payload?.documentId === documentId) {
        if (payload.status !== 'pending') closePopup();
        signal(payload);
      }
    });
    const offStatus = notificationStream.onStatus(setStreamStatus);
    return () => { off(); offStatus(); };
  }, [active, documentId, signal, closePopup]);

  // Hints: return page postMessage + tab focus.
  useEffect(() => {
    if (!active) return undefined;
    const onMessage = (e) => {
      if (e.data?.type !== 'esign-return') return;
      if (e.data.documentId && documentId && e.data.documentId !== documentId) return;
      signal(null);
    };
    const onFocus = () => signal(null);
    window.addEventListener('message', onMessage);
    window.addEventListener('focus', onFocus);
    return () => { window.removeEventListener('message', onMessage); window.removeEventListener('focus', onFocus); };
  }, [active, documentId, signal]);

  // Safety net only without a live stream.
  useEffect(() => {
    if (!active || streamStatus === 'live') return undefined;
    const t = setInterval(() => signal(null), DEGRADED_RESYNC_MS);
    return () => clearInterval(t);
  }, [active, streamStatus, signal]);

  useEffect(() => { if (!active) { closePopup(); setPopupClosed(false); setPopupBlocked(false); } }, [active, closePopup]);
  useEffect(() => () => closePopup(), [closePopup]);

  return { open, closePopup, popupBlocked, popupClosed, streamLive: streamStatus === 'live' };
}
